import { useEffect, useState, useRef } from 'react';
import { API, Auth } from 'aws-amplify';

import {
  Heading,
  Text,
  View,
  useAuthenticator,
  Loader,
  Collection,
  Button
} from '@aws-amplify/ui-react';

const POLL_INTERVAL = 3000; // Poll every 3 seconds
const REPORT_CACHE_DURATION = 5 * 60 * 1000; // Cache reports for 5 minutes

export function CampaignsReport({ profiles }) {

  const [isLoading, setLoading] = useState(true);
  const [marketingData, setMarketingData] = useState([]);
  const [reportStatus, setReportStatus] = useState(''); // 'processing', 'completed', 'error'
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const { user } = useAuthenticator((context) => [context.user]);
  const isGenerating = useRef(false);
  const pollingTimeoutId = useRef(null);

  async function generateNewReport() {
    // Prevent multiple simultaneous requests
    if (isGenerating.current) {
      //console.log('Report generation already in progress, skipping...');
      return;
    }

    isGenerating.current = true;
    setLoading(true);
    setReportStatus('processing');
    setError(null);
    
    var userLocal = undefined;
    try {
      userLocal = await Auth.currentAuthenticatedUser();
    } catch (error) {
      userLocal = user;
    };

    try {
      const requestReport = {
        headers: {
          Authorization: `${userLocal.signInUserSession.idToken.jwtToken}`
        },
        body: {
          reportDate: new Date().toISOString().split('T')[0], // Current date
          profileUrl: profiles?.[0]?.SK || ""
        }
      };
      
      const response = await API.post("gcAPI", `/dashboard-campaigns-report`, requestReport);
      //console.log("Report generation started:", response);

      if (response.reportId) {
        // Store reportId in localStorage with timestamp
        localStorage.setItem('campaignsReportId', response.reportId);
        localStorage.setItem('campaignsReportTimestamp', Date.now().toString());
        
        // Start polling for status
        pollReportStatus(response.reportId, userLocal);
      }
    } catch (e) {
      //console.log('Problem calling API:', e);
      setError('Failed to start report generation');
      setLoading(false);
      setReportStatus('error');
      isGenerating.current = false;
    }
  }

  async function pollReportStatus(reportId, userLocal) {
    // Clear any existing timeout
    if (pollingTimeoutId.current) {
      clearTimeout(pollingTimeoutId.current);
      pollingTimeoutId.current = null;
    }

    try {
      const statusRequest = {
        headers: {
          Authorization: `${userLocal.signInUserSession.idToken.jwtToken}`
        },
        body: {
          checkStatus: true,
          reportId: reportId
        }
      };

      const statusResponse = await API.post("gcAPI", `/dashboard-campaigns-report`, statusRequest);
      //console.log("Status response:", statusResponse);

      if (statusResponse.status === 'completed') {
        // Fetch the report data from signed URL
        const reportData = await fetch(statusResponse.reportUrl);
        const reportJson = await reportData.json();
        
        //console.log("Report data:", reportJson);
        
        var returnResponse = reportJson.Data;

        // Process parent campaigns
        for (var i = 0; i < returnResponse.length; i++) {
          if ("chainedCampaigns" in returnResponse[i]) {
            for (var j = 0; j < returnResponse[i].chainedCampaigns.length; j++) {
              for (var k = 0; k < returnResponse.length; k++) {
                if (returnResponse[k].SK === returnResponse[i].chainedCampaigns[j]) {
                  if (!("parentCampaign" in returnResponse[k]))
                    returnResponse[k].parentCampaign = returnResponse[i].SK;
                };
              };
            };
          };
        };

        await setMarketingData(returnResponse);
        setReportStatus('completed');
        setLoading(false);
        isGenerating.current = false;
        // Stop polling - report is complete
        return;
      } else if (statusResponse.status === 'processing') {
        setProgress(statusResponse.progress || 'Processing...');
        // Continue polling
        pollingTimeoutId.current = setTimeout(() => pollReportStatus(reportId, userLocal), POLL_INTERVAL);
      } else if (statusResponse.status === 'error') {
        setError(statusResponse.error || 'Report generation failed');
        setReportStatus('error');
        setLoading(false);
        isGenerating.current = false;
        // Clear cached reportId on error
        localStorage.removeItem('campaignsReportId');
        localStorage.removeItem('campaignsReportTimestamp');
        // Stop polling - error occurred
        return;
      } else if (statusResponse.status === 'not_found') {
        setError('Report not found');
        setReportStatus('error');
        setLoading(false);
        isGenerating.current = false;
        localStorage.removeItem('campaignsReportId');
        localStorage.removeItem('campaignsReportTimestamp');
        // Stop polling - report not found
        return;
      }
    } catch (e) {
      //console.log('Problem polling status:', e);
      setError('Failed to check report status');
      setReportStatus('error');
      setLoading(false);
      isGenerating.current = false;
    }
  }

  async function checkForExistingReport() {
    // Prevent multiple simultaneous requests
    if (isGenerating.current) {
      //console.log('Report generation already in progress, skipping check...');
      return true; // Return true to prevent new report generation
    }

    const cachedReportId = localStorage.getItem('campaignsReportId');
    const cachedTimestamp = localStorage.getItem('campaignsReportTimestamp');
    
    if (cachedReportId && cachedTimestamp) {
      const age = Date.now() - parseInt(cachedTimestamp);
      
      // If report is less than 5 minutes old, try to use it
      if (age < REPORT_CACHE_DURATION) {
        //console.log('Found recent report, checking status:', cachedReportId);
        var userLocal = undefined;
        try {
          userLocal = await Auth.currentAuthenticatedUser();
        } catch (error) {
          userLocal = user;
        };
        
        isGenerating.current = true;
        setReportStatus('processing');
        pollReportStatus(cachedReportId, userLocal);
        return true;
      } else {
        // Report is too old, clear it
        localStorage.removeItem('campaignsReportId');
        localStorage.removeItem('campaignsReportTimestamp');
      }
    }
    
    return false;
  }

  function numberCompleted(tasks) {
    var completed = 0;
    tasks.forEach(task => {
      if (task.stage === 'completed') completed++;
    });
    return completed;
  };

  useEffect(() => {
    // Check for existing recent report first
    const initReport = async () => {
      const hasExisting = await checkForExistingReport();
      if (!hasExisting) {
        // No recent report, generate new one
        generateNewReport();
      }
    };
    
    if (marketingData.length < 1 && !isGenerating.current) {
      initReport();
    }

    // Cleanup function to clear timeout on unmount
    return () => {
      if (pollingTimeoutId.current) {
        clearTimeout(pollingTimeoutId.current);
        pollingTimeoutId.current = null;
      }
    };
    //eslint-disable-next-line
  }, []);

  function colorLine(item) {
    if (item.requestType.indexOf("PAID-") > -1) return "red";
    if (item.requestType==="PAID") return "orange";
    if ("parentCampaign" in item) return "blue";
    if (("taskSK" in item) && item.taskSK.startsWith('TASK POST')) return "gray";
    return "black";
  };
    
  function CampaignsReport() {
    var result = marketingData;
    //console.log("result:", result);
    //result is an array of objects with RequestTime property as a string ISO date
    //sort by RequestTime so that the most recent is first

    result.sort((a, b) => {
      if (a.RequestTime < b.RequestTime) return 1;
      if (a.RequestTime > b.RequestTime) return -1;
      return 0;
    });

    //console.log("result:", result);

    return (
      <View>
        <Collection
          direction='column'
          type='list'
          items={result}
        >
          {(item, index) => (
            <Text key={index} style={{color: colorLine(item)}}>
              {item.SK} {item.RequestTime} {item.stage} {item.requestType} {item.requestIPCountry} {numberCompleted(item.tasks)} / {item.tasks.length} needed: {item.quantity - numberCompleted(item.tasks)} {item.taskPossible} {item.campaignUpdateStage} {item.currentStarsBalance} {("pods" in item && item.pods.length>0) ? "pods:"+item.pods : ""} {item.parentCampaign}
            </Text>
          )}
        </Collection>
      </View>
    );
  }

  //console.log("profiles[0].SK:", profiles[0].SK);//===tasks[0].uuid

  return (
    <View align="center">
      <Heading level={3} fontWeight={'bold'} textAlign="center">
        Campaigns Report for last 30 days
      </Heading>
      <View align="center">
        <Text variation="secondary">
          {reportStatus === 'processing' && `Generating report... ${progress}`}
          {reportStatus === 'completed' && `See the results of all ${marketingData ? marketingData.length : "..."} campaigns`}
          {reportStatus === 'error' && error}
          <br />
          {profiles && profiles.length > 0 ? profiles[0].SK : ""}
        </Text>
      </View>
      {reportStatus === 'completed' && (
        <View align="center" marginTop="1rem">
          <Button onClick={() => {
            // Clear cache and reset state for manual regeneration
            localStorage.removeItem('campaignsReportId');
            localStorage.removeItem('campaignsReportTimestamp');
            isGenerating.current = false;
            setMarketingData([]);
            generateNewReport();
          }} variation="primary" size="small">
            Regenerate Report
          </Button>
        </View>
      )}
      <br />
      <br />
      {isLoading ? (
        <View>
          <Loader variation="linear" />
          {progress && <Text textAlign="center" marginTop="1rem">{progress}</Text>}
        </View>
      ) : (
        <CampaignsReport />
      )}
    </View>
  )
}